import { useMutation } from '@apollo/client';
import React from 'react';

import { Button, Popup, PopupSection, Text } from '@flixbus/honeycomb-react';

import { AUXILIARY_WA_STATUS } from 'common/ActivityChip/const';
import { WA_AUXILIARY_SUCCESS_RESPONSE } from 'constants/activityTypes';
import { standardError } from 'global/commonMessages.js';
import { notificationStore } from 'global/NotificationStore.js';

import { updateAuxiliaryWASuccess } from './const';
import { UPDATE_AUXILIARY_WA_STATUS } from './updateAuxiliaryWorkAssignmentStatus';

import styles from './styles.module.css';

/**
 * PROPS (injected by ActiveElements + page):
 * - initialData?: { id?: string, version?: string|number }
 * - onActionSuccess?: () => void
 * - onClose: () => void   (injected by RootActiveElementsHost)
 */

const ModalDeleteActivity = ({ initialData = {}, onActionSuccess = () => {}, onClose }) => {
	const [updateAuxiliaryWAStatus, { loading }] = useMutation(UPDATE_AUXILIARY_WA_STATUS, {
		onCompleted: async (data) => {
			const response = data?.updateAuxiliaryWorkAssignmentStatus;

			if (response?.__typename === WA_AUXILIARY_SUCCESS_RESPONSE) {
				notificationStore.setNotification(updateAuxiliaryWASuccess);
				await Promise.resolve(onActionSuccess(response?.workAssignment?.fromTimestamp));
				onClose();
				return;
			}

			notificationStore.setNotification(standardError);
		},
		onError: () => {
			notificationStore.setNotification(standardError);
		},
	});

	const confirm = () => {
		if (initialData?.id === null || initialData?.version === null) {
			notificationStore.setNotification(standardError);
			return;
		}

		updateAuxiliaryWAStatus({
			variables: {
				input: {
					workAssignmentKey: {
						id: initialData.id,
						version: initialData.version,
					},
					status: AUXILIARY_WA_STATUS.rejected,
					reason: initialData.note ?? '',
				},
			},
		});
	};

	const handleClose = () => {
		onClose();
	};
	const cannotCancelAuxiliaryWA = initialData.cannotCancelAuxiliaryWA;

	return (
		<Popup active id="modal-delete-activity" onOverlayClick={handleClose}>
			<PopupSection type="title">
				{!cannotCancelAuxiliaryWA ? 'Cancel this activity?' : 'Activity cannot be cancelled'}
			</PopupSection>

			<PopupSection extraClasses={styles.antiScroll} type="content">
				<Text>
					{!cannotCancelAuxiliaryWA
						? 'This action cannot be undone. You will not be able to recover this activity once cancelled.'
						: 'Cancellation is only available for future activities. This activity has already occurred.'}
				</Text>
			</PopupSection>

			<PopupSection type="actions">
				<Button appearance={cannotCancelAuxiliaryWA ? 'primary' : ''} onClick={handleClose}>
					<strong>{!cannotCancelAuxiliaryWA ? "Don't cancel" : 'Got it'}</strong>
				</Button>

				{!cannotCancelAuxiliaryWA && (
					<Button appearance="primary" onClick={confirm} loading={loading}>
						<strong>Yes, cancel activity</strong>
					</Button>
				)}
			</PopupSection>
		</Popup>
	);
};

export default ModalDeleteActivity;
